/*
 * Shared state for connector OAuth round trips (ported from the legacy dashboard's
 * utils/oauthFlow.ts). Every provider hands control back to the backend callback, which
 * redirects to one fixed path (`/connector-setup`). Before leaving for the provider we
 * record which connector we started, when, and from where; the landing page reads it back.
 * Session storage only: nothing here is a token or a user value.
 */

const PENDING_CONNECTOR_KEY = 'pending_oauth_connector';
const PENDING_STARTED_AT_KEY = 'pending_oauth_started_at';
const RETURN_TO_KEY = 'oauth_return_to';
const SHOPIFY_LAUNCH_KEY = 'shopify_launch_shop';

/** A pending marker older than this is stale: the user never came back from the provider. */
const PENDING_FRESH_MS = 30 * 60 * 1000;

function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // Storage blocked: the flow still works, the result is matched on the provider name alone.
  }
}

/** "Meta-Ads" → "meta_ads". */
export function normalizeOAuthConnector(value: string | null | undefined): string | null {
  return value ? value.trim().toLowerCase().replace(/-/g, '_') : null;
}

/** Called right before navigating to the provider's authorize URL. */
export function markPendingOAuth(connectorId: string): void {
  write(PENDING_CONNECTOR_KEY, normalizeOAuthConnector(connectorId) ?? connectorId);
  write(PENDING_STARTED_AT_KEY, String(Date.now()));
  write(RETURN_TO_KEY, window.location.pathname);
}

export function clearPendingOAuth(): void {
  write(PENDING_CONNECTOR_KEY, null);
  write(PENDING_STARTED_AT_KEY, null);
  write(RETURN_TO_KEY, null);
}

/**
 * The connector started less than 30 minutes ago, normalised, or null. A stale marker is
 * cleared so it never claims a later, unrelated result.
 */
export function readPendingOAuth(): string | null {
  const connectorRaw = read(PENDING_CONNECTOR_KEY);
  const startedAtRaw = read(PENDING_STARTED_AT_KEY);
  const startedAt = startedAtRaw ? Number(startedAtRaw) : 0;
  const fresh = Number.isFinite(startedAt) && startedAt > 0 && Date.now() - startedAt < PENDING_FRESH_MS;
  if (!fresh && (connectorRaw || startedAtRaw)) clearPendingOAuth();
  return fresh ? normalizeOAuthConnector(connectorRaw) : null;
}

/**
 * The path the OAuth flow started from, consumed once. Null when nothing was stored, or
 * when the value is not a same-origin absolute path (guards against an open redirect).
 */
export function takeOAuthReturnPath(): string | null {
  const stored = read(RETURN_TO_KEY);
  write(RETURN_TO_KEY, null);
  if (!stored || !stored.startsWith('/') || stored.startsWith('//')) return null;
  return stored;
}

/** A Shopify app launch (/sign-up?connector=shopify&shop=…) keeps its store until Connect Data installs it. */
export function stashShopifyLaunch(shop: string): void {
  write(SHOPIFY_LAUNCH_KEY, shop);
}

/** The launched store, consumed once. */
export function takeShopifyLaunch(): string | null {
  const shop = read(SHOPIFY_LAUNCH_KEY);
  write(SHOPIFY_LAUNCH_KEY, null);
  return shop?.trim() ? shop.trim() : null;
}
